import { ArrowForwardIcon } from "@chakra-ui/icons";
import {
  VStack,
  GridItem,
  Heading,
  Button,
  Divider,
  ButtonGroup,
  Text,
  Box,
  Alert,
  AlertIcon,
  AlertDescription,
  Link,
} from "@chakra-ui/react";
import { Formik } from "formik";
import React from "react";
import TextInput from "../../form/fields/text";
import Section from "../../form/section";
import {
  primaryContactValidation,
  VicFormsRegistrationScreenComponent,
  VicFormsRegistrationScreens,
} from "./models";
import { submitVicFormsRegistration } from "./utils";

const PrimaryContact: React.FC<VicFormsRegistrationScreenComponent> = ({
  onSubmit,
  onBack,
  initialValues,
  wizardData,
}) => {
  const [errorMessage, setErrorMessage] = React.useState<string | undefined>();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={primaryContactValidation}
      onSubmit={async (values) => {
        setErrorMessage(undefined);

        try {
          await submitVicFormsRegistration({
            ...wizardData,
            PRIMARY_CONTACT: values,
          });
          onSubmit(values, VicFormsRegistrationScreens.SUCCESS);
        } catch (e) {
          setErrorMessage(
            (e as Error).message ||
              `Something went wrong when creating your VicForms account.`
          );
          throw new Error(`Cannot submit`);
        }
      }}
    >
      {({ handleSubmit, values, isSubmitting }) => {
        return (
          <form style={{ width: "100%" }} noValidate onSubmit={handleSubmit}>
            <VStack spacing={4} p={2} align={"stretch"} divider={<Divider />}>
              <VStack align="stretch" spacing={4}>
                <Heading>Add a primary contact</Heading>
                <Text>
                  Great! Now let's set up the primary contact for{" "}
                  <strong>{wizardData.ORGANISATION.organisationName}</strong>.
                </Text>
              </VStack>
              <Section title="Primary contact">
                <GridItem colSpan={4}>
                  <Alert status="info">
                    These contact details will be on every form your
                    organisation creates in VicForms.
                  </Alert>
                </GridItem>
                <GridItem rowSpan={1} colSpan={[4, 2]}>
                  <TextInput name="firstName" label="First Name" isRequired />
                </GridItem>
                <GridItem rowSpan={1} colSpan={[4, 2]}>
                  <TextInput name="lastName" label="Last Name" isRequired />
                </GridItem>

                <GridItem rowSpan={1} colSpan={[4, 2]}>
                  <TextInput name="phone" type="tel" label="Phone" isRequired />
                </GridItem>

                <GridItem rowSpan={1} colSpan={[4, 2]}>
                  <TextInput
                    name="mobile"
                    type="tel"
                    label="Mobile"
                    isRequired
                  />
                </GridItem>

                <GridItem rowSpan={1} colSpan={[4, 2]}>
                  <TextInput name="fax" type="tel" label="Fax" />
                </GridItem>
              </Section>

              <ButtonGroup justifyContent={"flex-end"}>
                <Button
                  isDisabled={isSubmitting}
                  onClick={() => onBack(values)}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  rightIcon={<ArrowForwardIcon />}
                  colorScheme={"primary"}
                  isLoading={isSubmitting}
                >
                  Create account
                </Button>
              </ButtonGroup>

              {errorMessage && (
                <Alert status="error">
                  <AlertIcon />
                  <Box flex="1">
                    <AlertDescription display="block">
                      <Text>{errorMessage}</Text>
                      Contact support at{" "}
                      <Link href="mailto:support@hutly.com">
                        support@hutly.com
                      </Link>
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </VStack>
          </form>
        );
      }}
    </Formik>
  );
};

export default PrimaryContact;
